import { addDays, petStage, todayIso, useBackpack, useClassPet } from '@shkola/api';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DARK, GRAD, INK, INK_SOFT, PAPER, SubjectArt, grad } from '../design/forms';
import { plain } from './home/model';

/*
 * «Форми 2» (28.10): the class's pet as a soft mint card — it grows as the class works, so its picture is
 * the sprout, big and quiet behind; the stage as a label, how far to the next one as a thin dark line,
 * this week's care as soft labels. No stickers and no emoji any more.
 */

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const weekdayKey = (iso: string) =>
  WEEKDAY_KEYS[(new Date(`${iso}T12:00:00Z`).getUTCDay() + 6) % 7] ?? 'mon';

/** A label on the mint: a soft white rectangle. */
function Tag({ children }: { children: ReactNode }) {
  return (
    <span
      className="inline-flex h-7 items-center rounded-[8px] px-2.5 text-[13px] leading-none font-medium"
      style={{ backgroundColor: 'rgb(255 255 255 / 0.78)', color: GRAD.mint.ink }}
    >
      {children}
    </span>
  );
}

/** «Питомець класу»: the class's dragon, its stage and this week's care — no names. */
export function ClassPetCard({
  classId,
  classLabel,
  className = '',
}: {
  classId: string;
  classLabel: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const pet = useClassPet(classId);
  if (!pet.data) return null;
  const { stage, next, progress } = petStage(pet.data.points, pet.data.students);
  const week = pet.data;
  return (
    <section
      className={`relative isolate flex flex-col gap-4 overflow-hidden rounded-[28px] p-5 md:p-6 ${className}`}
      style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
    >
      <SubjectArt
        icon="Sprout"
        tone="mint"
        size={170}
        ghost
        className="pointer-events-none absolute -top-6 -right-6 -z-10"
      />
      <div className="min-w-0 pr-16">
        <p className="text-[13px] font-medium">{t('pet.title')}</p>
        <h2
          className="mt-0.5 font-display text-[30px] leading-[36px] font-light tracking-[-0.03em]"
          style={{ color: INK }}
        >
          {t('pet.name', { class: classLabel })}
        </h2>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Tag>{t(`pet.stages.${stage.key}`)}</Tag>
        </div>
      </div>
      <div>
        <div className="h-[3px] rounded-full bg-white/70">
          <div
            className="h-full rounded-full transition-[width] duration-1000"
            style={{ width: `${Math.round(progress * 100)}%`, backgroundColor: DARK }}
          />
        </div>
        <p className="mt-1.5 text-[13px]">
          {next
            ? t('pet.next', {
                stage: t(`pet.stages.${next.key}`),
                percent: Math.round(progress * 100),
              })
            : t('pet.max')}
        </p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Tag>
          {t('pet.week')}: {t('pet.weekPoints', { count: week.weekPoints })}
        </Tag>
        {week.weekHomework !== null && (
          <Tag>{t('pet.homework', { percent: Math.round(week.weekHomework * 100) })}</Tag>
        )}
        {week.weekAttendance !== null && (
          <Tag>{t('pet.attendance', { percent: Math.round(week.weekAttendance * 100) })}</Tag>
        )}
        {week.weekTests > 0 && <Tag>{t('pet.tests', { count: week.weekTests })}</Tag>}
      </div>
      <p className="text-[13px] leading-[19px]" style={{ color: INK_SOFT }}>
        {t('pet.how')}
      </p>
    </section>
  );
}

/** «Рюкзак на завтра»: what the next school day's lessons need, the teacher's words first. */
export function BackpackCard({
  schoolId,
  studentId,
  className = '',
}: {
  schoolId: string;
  studentId: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const pack = useBackpack(schoolId, studentId);
  if (!pack.date) return null;
  return (
    <section className={`flex flex-col gap-3 rounded-[28px] bg-white p-5 md:p-6 ${className}`}>
      <h2
        className="font-display text-[30px] leading-[36px] font-light tracking-[-0.03em]"
        style={{ color: INK }}
      >
        {t('backpack.title', {
          day:
            pack.date === addDays(todayIso(), 1)
              ? t('backpack.tomorrow')
              : t(`student.on.${weekdayKey(pack.date)}`),
        })}
      </h2>
      {pack.items.length === 0 ? (
        <p className="text-[15px]" style={{ color: INK_SOFT }}>
          {t('backpack.nothing')}
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {pack.items.map((item) => (
            <li
              key={item.label}
              className="rounded-[16px] px-4 py-2.5"
              style={
                item.fromTeacher ? { backgroundImage: grad('butter') } : { backgroundColor: PAPER }
              }
            >
              <span className="block text-[15px] font-medium" style={{ color: INK }}>
                {plain(item.label)}
              </span>
              <span className="block text-[12px]" style={{ color: INK_SOFT }}>
                {item.subjects.join(', ')}
                {item.fromTeacher && ` · ${t('backpack.teacher')}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[14px]" style={{ color: INK_SOFT }}>
        {t('backpack.books', { subjects: pack.subjects.join(', ').toLowerCase() })}
      </p>
    </section>
  );
}
